import React, { useEffect, useState } from 'react';
import { Leer } from '../components/hilfe/Tipp';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FolderOpen, Loader2, MessageSquare, FileText, CalendarDays, FileSignature, Wallet, ScrollText, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { PortalKopf, PortalSeite, eur, datum } from '../components/portal/PortalKopf';
import { portalAktualisieren } from '../components/portal/PortalDock';
import { PortalAblageStreifen } from '../components/portal/PortalAblage';
import { PortalUnterlagen } from '../components/portal/PortalUnterlagen';
import { PortalZustimmungen } from '../components/portal/PortalZustimmungen';
import i18n from 'i18next';

const ICON = { sachstand: ScrollText, dokument: FileText, nachricht: MessageSquare, termin: CalendarDays, unterschrift: FileSignature, zahlung: Wallet };

const Liste = ({ akten, t }) => (
  <ul className="space-y-2" data-testid="portal-akten-liste">
    {akten.map((a) => (
      <li key={a.case_id}>
        <Link to={`/portal/akten/${a.case_id}`} data-testid={`portal-akte-${a.case_id}`} className={`group flex items-start gap-3 p-4 bg-white rounded-2xl border border-gray-200 hover:border-[#b8893d]/70 transition-colors ${a.abgeschlossen ? 'opacity-70' : ''}`}>
          <FolderOpen className="w-5 h-5 mt-0.5 text-[#b8893d]" strokeWidth={1.75} />
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 text-sm font-medium text-[#1B3A5C]">{a.aktenzeichen || a.rechtsgebiet}
              {a.neu && <span className="px-2 py-0.5 rounded-full bg-[#b8893d] text-[#0B1724] text-[10px] font-semibold" data-testid={`portal-akte-neu-${a.case_id}`}>{t('portal.neu', 'neu')}</span>}
              {a.abgeschlossen && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px]">{t('portal.abgeschlossen', 'abgeschlossen')}</span>}</span>
            <span className="block text-xs text-gray-500">{[a.rechtsgebiet, a.gegner].filter(Boolean).join(' · ')}</span>
            <span className="block mt-1 text-sm text-[#34536b] line-clamp-2">{a.sachstand || t('portal.kein_sachstand', 'Noch kein Sachstand eingetragen.')}</span>
          </span>
          <ChevronRight className="w-4 h-4 mt-1 text-gray-400 group-hover:text-[#b8893d]" />
        </Link>
      </li>
    ))}
  </ul>
);

const Chronik = ({ events, t }) => (
  <ol className="relative ml-3 border-l-2 border-transparent [border-image:linear-gradient(to_bottom,#e0b56a,#1B3A5C33)_1] space-y-4" data-testid="portal-chronik">
    {events.map((e, i) => {
      const Icon = ICON[e.typ] || FileText;
      const inhalt = (<><span className="block text-sm text-[#1B3A5C] font-medium">{e.titel}{e.betrag != null ? ` · ${eur(e.betrag)}` : ''}</span>
        {e.text && <span className="block text-sm text-[#34536b] whitespace-pre-line">{e.text}</span>}
        <span className="block text-[11px] text-gray-500 mt-0.5">{datum(e.datum)}</span></>);
      return (
        <li key={i} className="pl-6 relative" data-testid={`portal-chronik-${e.typ}-${i}`}>
          <span className="absolute -left-[13px] top-0.5 w-6 h-6 rounded-full bg-white border border-[#e0b56a] shadow-[0_0_10px_rgba(224,181,106,0.6)] flex items-center justify-center"><Icon className="w-3 h-3 text-[#b8893d]" /></span>
          {e.link ? <Link to={e.link} className="block hover:opacity-80">{inhalt}<span className="text-[11px] text-[#b8893d]">{t('portal.zum_chat', 'Im Chat öffnen')} →</span></Link> : inhalt}
        </li>
      );
    })}
    {!events.length && <li className="pl-6 text-sm text-gray-500">{t('portal.keine_ereignisse', 'Noch keine Ereignisse.')}</li>}
  </ol>
);

const Detail = ({ caseId, t }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.get(`/portal/akten/${caseId}`).then((r) => { setD(r.data); portalAktualisieren(); }).catch((e) => setErr(e?.response?.data?.detail || 'Fehler')); }, [caseId]);
  if (err) return <p className="text-sm text-rose-700" data-testid="portal-akte-fehler">{err}</p>;
  if (!d) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>;
  const a = d.akte;
  return (
    <div className="space-y-6" data-testid="portal-akte-detail">
      <section className="p-5 bg-white rounded-2xl border border-[#b8893d]/40 shadow-[0_10px_40px_-24px_rgba(11,23,36,0.35)]" data-testid="portal-sachstand">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#b8893d]">{t('portal.sachstand', 'Aktueller Sachstand')}{a.sachstand_am ? ` · ${datum(a.sachstand_am)}` : ''}</p>
        <p className="mt-2 text-[#1B3A5C] whitespace-pre-line">{a.sachstand || t('portal.kein_sachstand', 'Noch kein Sachstand eingetragen.')}</p>
        <Link to={`/meine-akten/${a.case_id}`} data-testid="portal-akte-nachricht" className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-[10px] bg-[#1B3A5C] text-white text-sm hover:bg-[#2A5280] transition-colors"><MessageSquare className="w-4 h-4" />{t('portal.nachricht_akte', 'Nachricht zu dieser Akte')}</Link>
      </section>
      <PortalUnterlagen caseId={a.case_id} lang={i18n?.language} />
      <PortalAblageStreifen text="Unterlagen zu dieser Akte? Einfach Dateien auf die Seite ziehen – sie landen direkt in dieser Akte bei der Kanzlei." />
      <PortalZustimmungen caseId={a.case_id} />
      <section><h2 className="text-base md:text-lg font-serif text-[#1B3A5C] mb-4">{t('portal.chronik', 'Chronologie')}</h2><Chronik events={d.chronik} t={t} /></section>
    </div>
  );
};

export default function PortalAkten() {
  const { t } = useTranslation();
  const { caseId } = useParams();
  const [akten, setAkten] = useState(null);
  useEffect(() => { api.get('/portal/uebersicht').then((r) => setAkten(r.data.akten)).catch(() => setAkten([])); }, [caseId]);
  const akte = caseId && (akten || []).find((a) => a.case_id === caseId);
  return (
    <PortalSeite testId="portal-akten">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] pb-28">
        <PortalKopf kicker={t('portal.tab.akten', 'Akten')} titel={caseId ? (akte?.aktenzeichen || akte?.rechtsgebiet || t('portal.akte', 'Akte')) : t('portal.meine_akten', 'Ihre Akten')}>
          {caseId && <Link to="/portal/akten" className="mt-3 inline-flex items-center gap-1 text-xs text-white/70 hover:text-white" data-testid="portal-akten-zurueck"><ArrowLeft className="w-3.5 h-3.5" />{t('portal.alle_akten', 'Alle Akten')}</Link>}
        </PortalKopf>
        <div className="max-w-3xl mx-auto px-5 mt-8">
          {caseId ? <Detail key={caseId} caseId={caseId} t={t} />
            : !akten ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>
              : akten.length ? <Liste akten={akten} t={t} /> : <Leer icon={FolderOpen} testId="portal-akten-leer" titel={t('portal.keine_akten_titel', 'Noch keine Akten')} text={t('portal.keine_akten', 'Noch keine Akten. Sobald die Kanzlei eine Akte anlegt, erscheint sie hier.')} />}
        </div>
      </main>
    </PortalSeite>
  );
}
